import Graphic from '@arcgis/core/Graphic';

import { SnCamera, SnCameraStatus } from '@lib/api/entities';
import { Maps, SnLonLat } from '@lib/geo/helpers';
import {
  FsMap,
  FsMapMarker,
  FsMarkerSymbolType,
  IMapMarkerOptions,
  IMapMarkerStyleOptions,
  MapMarkerIcon,
} from '@lib/geo/map';
import { COLORS } from '@lib/ui/theme';

import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

const DEFAULT_STYLE = {
  scale: 1,
  strokeOpacity: 0,
  fillOpacity: 0,
  fillColor: COLORS['base-800'],
  strokeColor: COLORS['grey-400'],
  strokeWeight: 1,
  rotation: 90,
};

export class FsCameraMarker extends FsMapMarker {
  get isUav(): boolean {
    return this.camera?.isUav ?? false;
  }

  public id: string;
  public camera: SnCamera;
  public selected: boolean;
  public destination: {
    lat: number;
    lon: number;
  };

  private _distanceToDestination: number;
  private _deltaLat: number;
  private _deltaLng: number;
  private _expectedMoves: number;
  private _actualMoves: number;

  public static getIcon(camera: SnCamera, cluster?: any): MapMarkerIcon {
    let status = cluster ? 'Up' : 'Exists';
    const icon: MapMarkerIcon = {
      fontIcon: camera?.isAiEnabled ? `Camera${status}_AI` : `Icon_Camera${status}`,
      fontSet: 'fs',
    };
    if (camera?.isUav) {
      icon.fontIcon = 'Icon_UAV';
      return icon;
    }
    if (camera) {
      const ptz = camera.type === 'PTZ';
      if (camera.isParentCamera) {
        icon.fontIcon = 'quad_camera_lite';
        icon.color =
          camera.status === SnCameraStatus.Up
            ? '#67BD4D'
            : camera.status === SnCameraStatus.Down
            ? '#FF3535'
            : camera.status === SnCameraStatus.Unmanaged
            ? '#FFB028'
            : 'rgb(137, 137, 137);';
        // this.setBadge({
        //   value: camera.descendantCount + 1,
        //   color: 'secondary',
        //   positionX: 'after',
        //   positionY: 'below',
        // });
        return icon;
      }
      switch (camera.status) {
        case SnCameraStatus.Up:
          status = 'Up';
          break;
        case SnCameraStatus.Down:
          status = 'Down';
          break;
        case SnCameraStatus.Unmanaged:
          icon.fontIcon = 'CameraUnmanaged';
          return icon;
      }

      if (ptz) {
        icon.fontIcon = camera.isAiEnabled ? `Camera${status}_AI_PTZ` : `Camera${status}_PTZ`;
        return icon;
      }
    }
    icon.fontIcon = camera?.isAiEnabled ? `Camera${status}_AI` : `Icon_Camera${status}`;
    return icon;
  }

  constructor({
    map,
    camera,
    cluster = null,
    initialStyle,
    selected,
    markerOptions,
  }: {
    map: FsMap;
    camera?: SnCamera;
    cluster?: any;
    initialStyle?: IMapMarkerStyleOptions;
    selected?: boolean;
    markerOptions?: IMapMarkerOptions;
  }) {
    const icon = FsCameraMarker.getIcon(camera, cluster);
    super(
      map,
      {
        id: `camera-${camera?.id}`,
        type: FsMarkerSymbolType.CIRCLE,
        lonLat: cluster ? cluster.geometry.coordinates : camera.geometry.coordinates,
        title: camera ? camera.name : null,
        icon,
        color: 'rgb(103, 189, 77)',
        size: '22px',
        style: {
          ...DEFAULT_STYLE,
          scale: camera?.isAiEnabled ? 1.2 : 1,
          rotation: camera?.heading ? camera.heading : 90,
          ...initialStyle,
        },
        collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
        clickable: true,
        attributes: camera,
        ...markerOptions,
      },
      cluster,
    );
    this.camera = camera;
    this.selected = selected;

    if (this.defaultMapSource === Maps.GOOGLE) {
      const icon = this.content.querySelector('.fs__advanced-feature-icon');
      if (icon) {
        icon.classList.add('fs__camera-advanced-icon');
      }
    }

    if (cluster) {
      this.createBadge();
    }
    if (this.selected) {
      this.createSelectedBadge();
    }

    if (this.isUav) {
      if (this.defaultMapSource === Maps.GOOGLE) {
        this.content.classList.add('fs__camera-uav-marker');
      }

      this._handleUav();
    }
    this.setRotation(this.styles.rotation);
  }

  public setScale(scale: number): void {
    const style = this.styles;

    this.setStyle({ ...style, scale });
  }

  private _handleUav() {
    const color = this._getStatusColor();
    this.setBadgeStatus(color, {
      top: '28px',
      right: '-3px',
    });
  }

  private _getStatusColor(): string {
    return this.camera.status === SnCameraStatus.Up
      ? '#67BD4D'
      : this.camera.status === SnCameraStatus.Down
      ? '#FF3535'
      : this.camera.status === SnCameraStatus.Unmanaged
      ? '#FFB028'
      : 'rgb(137, 137, 137)';
  }

  public setDestination(latLng: { lat: number; lon: number }): void {
    if (this.destination) {
      if (this.defaultMapSource === Maps.GOOGLE) {
        (this.getMarker() as unknown as AdvancedMarkerElement).position = {
          lat: this.destination.lat,
          lng: this.destination.lon,
        };
      } else {
        (this.getMarker() as unknown as Graphic[]).forEach(
          (graphic) => (graphic.geometry = this.destination as unknown as __esri.Point),
        );
      }
    }
    this.destination = latLng;
  }

  public setDeltas(seconds: number): void {
    const destination = this.destination;
    const current = this.getLonLat();
    this._expectedMoves = 10 * seconds;
    this._actualMoves = 0;
    this._deltaLat = (destination.lat - current.lat) / this._expectedMoves;
    this._deltaLng = (destination.lon - current.lon) / this._expectedMoves;
  }

  public move(): void {
    if (this._actualMoves >= this._expectedMoves) {
      this.setLonLat(new SnLonLat(this.destination.lon, this.destination.lat));
      return;
    }
    this._actualMoves++;
    const current = this.getLonLat();
    const destination = this.destination;
    const meters2Miles = 0.000621371192;
    this._distanceToDestination =
      Math.round(
        google.maps.geometry.spherical.computeDistanceBetween(
          { lat: current.lat, lng: current.lon },
          { lat: this.destination.lat, lng: this.destination.lon },
        ) *
          meters2Miles *
          10,
      ) / 10;
    if (!this.map?.contains(current)) {
      this._actualMoves = this._expectedMoves;
      this.setLonLat(new SnLonLat(destination.lon, destination.lat));
      return;
    }
    let newLat = current.lat + this._deltaLat;
    if (current.lat === destination.lat) {
      newLat = destination.lat;
    }
    let newLng = current.lon + this._deltaLng;
    if (current.lon === destination.lon) {
      newLng = destination.lon;
    }
    this.setLonLat(new SnLonLat(newLng, newLat));
    if (!this.camera?.isUav && this._distanceToDestination > 0.1) {
      this.moveToDestination();
    }
  }

  public moveToDestination(): void {
    if (!this.destination) {
      return;
    }
    this.setLonLat(new SnLonLat(this.destination.lon, this.destination.lat));
  }
}
